/**
 * UR Brand System — Phase 5b Customer Concept Review.
 * Shows ONLY Gate-1-approved concepts (customer_facing). Simple plain
 * language. "Neither feels right yet" routes through backend classifier.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { customerApi } from "@/lib/brandApi";

const T = {
  en: {
    heading: "Pick your identity direction",
    whyWorks: "Why this works for your brand",
    choose: "Choose this concept", neither: "Neither feels right yet",
    whatLike: "What do you like?",
    likeOpts: ["The overall look", "The colors", "The logo",
               "How it looks on the examples", "It simply feels right for my business"],
    adjust: "Any small adjustment you'd like us to try? (optional)",
    reasonPrompt: "What doesn't feel right?",
    reasons: ["Too serious", "Too playful", "Too simple", "Too bold",
              "It doesn't feel like my business", "Something else"],
    submit: "Submit", yourName: "Your name",
    confirm: "I am the person authorized to approve this.",
    nothingYet: "Your concepts aren't ready yet.",
    loading: "Loading…", unauthorized: "This link is invalid or expired.",
    thanks: "Thank you — Ur Designs will be in touch.",
  },
};

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-black text-neutral-100">
      <header className="border-b border-neutral-800 px-4 sm:px-6 py-4 sticky top-0 bg-black/90 backdrop-blur z-10">
        <div className="flex items-center max-w-5xl mx-auto">
          <div className="font-black text-orange-500 tracking-tight">Ur Designs</div>
          <div className="ml-3 text-xs text-neutral-500 hidden sm:block">Identity Concepts</div>
        </div>
      </header>
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-10">{children}</main>
    </div>
  );
}

export default function CustomerConcepts() {
  const { projectId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const t = T.en;
  const api = useMemo(() => customerApi(token), [token]);
  const [concepts, setConcepts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [picked, setPicked] = useState("");
  const [likeChoice, setLikeChoice] = useState("");
  const [adjust, setAdjust] = useState("");
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("");  // "" | "pick" | "neither"
  const [name, setName] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (!token) { setErr("unauthorized"); setLoading(false); return; }
    api.get(`/${projectId}/concepts`).then((r) => {
      setConcepts(r.data || []);
    }).catch((e) => {
      const c = e?.response?.status;
      setErr(c === 401 || c === 404 ? "unauthorized" : (e?.response?.data?.detail || "load failed"));
    }).finally(() => setLoading(false));
  }, [api, projectId, token]);

  if (loading) return <Shell><p className="text-neutral-400">{t.loading}</p></Shell>;
  if (err === "unauthorized") return <Shell><p className="text-neutral-400" data-testid="concepts-unauthorized">{t.unauthorized}</p></Shell>;
  if (!concepts.length) return <Shell><p className="text-neutral-400" data-testid="concepts-nothing-yet">{t.nothingYet}</p></Shell>;
  if (sent) return <Shell>
    <div className="py-16 text-center" data-testid="concepts-thanks">
      <div className="text-5xl mb-6">✓</div>
      <h1 className="text-2xl font-black">{t.thanks}</h1>
    </div>
  </Shell>;

  const submit = async () => {
    if (!name.trim() || !confirm) {
      toast.error("Please enter your name and confirm."); return;
    }
    try {
      await api.post(`/${projectId}/concept-decision`, {
        concept_id: mode === "pick" ? picked : "",
        neither_feels_right: mode === "neither",
        like_choice: likeChoice,
        adjust_note: adjust,
        neither_reason: reason,
        customer_language: "en",
        approver_name: name,
        decision_maker_confirmed: !!confirm,
      });
      toast.success(t.thanks);
      setSent(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    }
  };

  return (
    <Shell>
      <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-8" data-testid="concepts-heading">
        {t.heading}
      </h1>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="concepts-grid">
        {concepts.map((c) => (
          <button key={c.concept_id} onClick={() => { setPicked(c.concept_id); setMode("pick"); }}
            data-testid={`concept-card-${c.concept_id}`}
            className={`block w-full text-left border rounded-xl overflow-hidden transition ${
              picked === c.concept_id ? "border-orange-500 bg-orange-500/5" :
              "border-neutral-800 hover:border-neutral-600 bg-neutral-950"
            }`}>
            {c.primary_logo_asset_url && (
              <div className="aspect-[4/3] bg-neutral-900 flex items-center justify-center">
                <img src={c.primary_logo_asset_url} alt="concept" className="max-h-[80%] max-w-[80%]" />
              </div>
            )}
            <div className="p-5">
              <div className="text-xs text-neutral-500 tracking-widest mb-2">CONCEPT {c.round_number}</div>
              <h3 className="font-black text-lg mb-2">{t.whyWorks}</h3>
              <ul className="text-sm text-neutral-400 space-y-1">
                {c.color_direction && <li>• {c.color_direction}</li>}
                {c.typography_direction && <li>• {c.typography_direction}</li>}
                {(c.applications || []).slice(0, 1).map((a, i) => a.note ? <li key={i}>• {a.note}</li> : null)}
              </ul>
            </div>
          </button>
        ))}
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3">
        <button onClick={() => setMode("neither")}
          data-testid="concepts-neither"
          className="w-full sm:w-auto px-5 py-3 border border-neutral-700 rounded-lg">
          {t.neither}
        </button>
      </div>

      {mode === "pick" && picked && (
        <div className="mt-10 border-t border-neutral-800 pt-8 space-y-6" data-testid="concept-feedback-panel">
          <p className="text-sm text-neutral-400">{t.whatLike}</p>
          <div className="space-y-2">
            {t.likeOpts.map((o) => (
              <label key={o} className="flex items-center gap-3 p-3 border border-neutral-800 rounded-lg cursor-pointer hover:border-neutral-700 text-sm">
                <input type="radio" name="like" checked={likeChoice === o} onChange={() => setLikeChoice(o)} />
                <span>{o}</span>
              </label>
            ))}
          </div>
          <div>
            <label className="block text-sm text-neutral-400 mb-2">{t.adjust}</label>
            <textarea value={adjust} onChange={(e) => setAdjust(e.target.value)}
              className="w-full min-h-[80px] bg-neutral-900 border border-neutral-800 rounded-lg p-3 text-sm"
              data-testid="adjust-note" />
          </div>
          <div className="p-4 bg-neutral-950 border border-neutral-800 rounded-lg space-y-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={t.yourName}
              data-testid="concept-approver"
              className="w-full bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm" />
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)}
                data-testid="concept-confirm" />
              <span>{t.confirm}</span>
            </label>
          </div>
          <button onClick={submit} disabled={!confirm || !name}
            data-testid="concept-submit"
            className="px-6 py-3 bg-orange-500 text-black font-black rounded-lg disabled:opacity-40">
            {t.submit}
          </button>
        </div>
      )}

      {mode === "neither" && (
        <div className="mt-10 border-t border-neutral-800 pt-8 space-y-6" data-testid="concept-neither-panel">
          <p className="text-sm text-neutral-400">{t.reasonPrompt}</p>
          <div className="space-y-2">
            {t.reasons.map((o) => (
              <label key={o} className="flex items-center gap-3 p-3 border border-neutral-800 rounded-lg cursor-pointer hover:border-neutral-700 text-sm">
                <input type="radio" name="reason" checked={reason === o} onChange={() => setReason(o)} />
                <span>{o}</span>
              </label>
            ))}
          </div>
          <button onClick={submit} disabled={!reason}
            data-testid="neither-submit"
            className="px-6 py-3 bg-orange-500 text-black font-black rounded-lg disabled:opacity-40">
            {t.submit}
          </button>
        </div>
      )}
    </Shell>
  );
}
